import { useMemo } from 'react';
import {
  GetAnimalEbvsQuery,
  GetAnimalMediaLotDocumentsQuery,
  useGetAnimalMediaLotDocumentsQuery,
} from '@/publicSearch/graphql/queries.generated';
import {
  MEDIA_KIND,
  buildDocumentProxyUrl,
  compareMediaDocuments,
  filterRedundantThumbnails,
  getDocumentIdentity,
  getDocumentMediaKind,
} from '@/publicSearch/utils/media';
import { buildAnimalLotEbvsQueryOptions } from './animalLotEbvsQuery';

type AnimalDocument =
  GetAnimalEbvsQuery['animalEbvs']['items'][number]['documents'][number];
type LotMediaRow =
  GetAnimalMediaLotDocumentsQuery['lotAnimalEbvs']['items'][number];
type LotDocument = LotMediaRow['documents'][number];
type LotAnimalDocument = LotMediaRow['animalDocuments'][number];

export type AnimalMediaItem = {
  id: string;
  kind: (typeof MEDIA_KIND)[keyof typeof MEDIA_KIND];
  fileName: string | null;
  isPrimary: boolean;
  source: 'animal' | 'lot';
  title: string;
  url: string;
  proxyUrl: string;
  saleId?: number;
  saleName?: string | null;
  saleDate?: string | null;
  lotNo?: string;
};

type UseAnimalMediaParams = {
  animalId?: string | number | null;
  animalDocuments?: AnimalDocument[];
  orgId?: string | number | null;
};

type SourceMeta = {
  source: 'animal' | 'lot';
  saleId?: number;
  saleName?: string | null;
  saleDate?: string | null;
  lotNo?: string;
};

function toMediaItem(
  document: AnimalDocument | LotDocument | LotAnimalDocument,
  societyId: string | number | null | undefined,
  sourceMeta: SourceMeta,
): AnimalMediaItem | null {
  if (document.isDeleted || !document.url) return null;

  const kind = getDocumentMediaKind(document);
  if (!kind) return null;

  const proxyUrl = buildDocumentProxyUrl(document.url, societyId);
  if (!proxyUrl) return null;

  const identity = getDocumentIdentity(document);
  const sourceSuffix =
    sourceMeta.source === 'lot' && sourceMeta.saleId != null && sourceMeta.lotNo
      ? `lot:${sourceMeta.saleId}:${sourceMeta.lotNo}`
      : sourceMeta.source;

  return {
    id: identity
      ? `${sourceMeta.source}:${identity}`
      : `${sourceSuffix}:${proxyUrl}`,
    kind,
    fileName: document.fileName ?? null,
    isPrimary: document.isPrimary,
    source: sourceMeta.source,
    title:
      sourceMeta.source === 'lot' ? `Lot ${sourceMeta.lotNo ?? ''}`.trim() : '',
    url: document.url,
    proxyUrl,
    saleId: sourceMeta.saleId,
    saleName: sourceMeta.saleName,
    saleDate: sourceMeta.saleDate,
    lotNo: sourceMeta.lotNo,
  };
}

export function useAnimalMedia({
  animalId,
  animalDocuments = [],
  orgId,
}: UseAnimalMediaParams) {
  const enabled = Boolean(animalId && orgId);

  const { data, loading, error } = useGetAnimalMediaLotDocumentsQuery(
    buildAnimalLotEbvsQueryOptions({ animalId, orgId, enabled }),
  );

  const items = useMemo<AnimalMediaItem[]>(() => {
    const merged = new Map<string, AnimalMediaItem>();

    const directDocs = [...animalDocuments].sort(compareMediaDocuments);
    directDocs.forEach((document) => {
      const item = toMediaItem(document, orgId, { source: 'animal' });
      if (!item) return;
      const key = getDocumentIdentity(document) ?? item.id;
      if (!merged.has(key)) {
        merged.set(key, item);
      }
    });

    (data?.lotAnimalEbvs.items ?? []).forEach((row) => {
      const sourceMeta = {
        source: 'lot' as const,
        saleId: row.saleId,
        saleName: row.sale?.saleName ?? null,
        saleDate: row.sale?.saleDate ? String(row.sale.saleDate) : null,
        lotNo: row.lotNo,
      };

      [...row.documents].sort(compareMediaDocuments).forEach((document) => {
        const item = toMediaItem(document, orgId, sourceMeta);
        if (!item) return;
        const key = getDocumentIdentity(document) ?? item.id;
        if (!merged.has(key)) {
          merged.set(key, item);
        }
      });

      [...row.animalDocuments]
        .sort(compareMediaDocuments)
        .forEach((document) => {
          const item = toMediaItem(document, orgId, sourceMeta);
          if (!item) return;
          const key = getDocumentIdentity(document) ?? item.id;
          if (!merged.has(key)) {
            merged.set(key, item);
          }
        });
    });

    const sorted = [...merged.values()].sort((a, b) => {
      const primaryDiff = Number(b.isPrimary) - Number(a.isPrimary);
      if (primaryDiff !== 0) return primaryDiff;

      if (a.source !== b.source) {
        return a.source === 'animal' ? -1 : 1;
      }

      const saleDiff = (b.saleId ?? 0) - (a.saleId ?? 0);
      if (saleDiff !== 0) return saleDiff;

      return a.title.localeCompare(b.title);
    });

    return filterRedundantThumbnails(sorted);
  }, [animalDocuments, data?.lotAnimalEbvs.items, orgId]);

  return {
    items,
    loading,
    error,
    hasMedia: items.length > 0,
  };
}
